import { useState } from "react";
import { TextInput, Text, View, type TextInputProps } from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { useInputFontSize } from "../utils/inputFontSize";
import { MoonFormField } from "./MoonFormField";
import { MoonFieldInput } from "./MoonFieldInput";

/**
 * Numeric text input — TextInput + numeric keyboard + integer/decimal
 * parsing. The first concrete consumer of MoonFieldInput.
 *
 * Replaces the hand-rolled "TextInput with keyboardType numeric inside a
 * bordered View" pattern that the audit (task 2.2) flagged in 6+ modals.
 *
 * Numeric semantics:
 *   - `value` is always the *parsed number* (or null when the field is
 *     empty). Callers don't deal with strings.
 *   - Internally tracks a string buffer to allow intermediate states like
 *     "12." while the user types a decimal.
 *   - On blur, the string is re-parsed and `onChange` fires with the
 *     clamped value (min / max applied).
 *   - Empty string → onChange(null) so a cleared input reads as "no value"
 *     rather than 0.
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / focused / error / disabled handled by MoonFieldInput +
 *     MoonFormField composition.
 *   - loading flips MoonFormField's child to a skeleton.
 */
type Props = {
  label: string;
  /** Optional icon rendered beside the label (forwarded to MoonFormField). */
  labelIcon?: LucideIcon;
  /** Supporting copy rendered directly below the label and above the input. */
  labelHint?: string;
  /** Parsed value. `null` means the field is empty. */
  value: number | null;
  /** Fires with the parsed value (clamped to min/max) or null. */
  onChange: (next: number | null) => void;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Number of decimal places allowed. 0 = integer-only (default). */
  decimals?: number;
  min?: number;
  max?: number;
  /** Optional prefix rendered inside the field (e.g. "$"). */
  prefix?: string;
  /** Optional suffix rendered inside the field (e.g. "kg"). */
  suffix?: string;
  /** Pass-through TextInput props for fine-grained control. */
  textInputProps?: Omit<
    TextInputProps,
    | "value"
    | "onChangeText"
    | "onFocus"
    | "onBlur"
    | "editable"
    | "keyboardType"
    | "placeholder"
    | "placeholderTextColor"
    | "style"
  >;
  testID?: string;
};

function formatForDisplay(value: number | null, decimals: number): string {
  if (value === null || Number.isNaN(value)) return "";
  return decimals > 0 ? value.toFixed(decimals) : String(Math.trunc(value));
}

function parseBuffer(buffer: string, decimals: number): number | null {
  const trimmed = buffer.trim();
  if (trimmed === "") return null;
  const parsed = decimals > 0 ? parseFloat(trimmed) : parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function clamp(value: number, min?: number, max?: number): number {
  if (min !== undefined && value < min) return min;
  if (max !== undefined && value > max) return max;
  return value;
}

export function MoonNumericField({
  label,
  labelIcon,
  labelHint,
  value,
  onChange,
  placeholder,
  error,
  hint,
  required,
  disabled,
  loading,
  decimals = 0,
  min,
  max,
  prefix,
  suffix,
  textInputProps,
  testID,
}: Props) {
  const fontSize = useInputFontSize();
  const [focused, setFocused] = useState(false);
  // Local buffer so the user can type intermediate states like "12." that
  // wouldn't survive a round-trip through parseFloat.
  const [buffer, setBuffer] = useState<string>(() =>
    formatForDisplay(value, decimals),
  );

  // Sync buffer when parent value changes from outside (e.g. form reset).
  // Only when not focused so we don't yank the cursor mid-typing.
  if (!focused && parseBuffer(buffer, decimals) !== value) {
    setBuffer(formatForDisplay(value, decimals));
  }

  const handleChangeText = (text: string) => {
    // Strip anything that isn't a digit, leading minus, or (when decimals>0)
    // a single decimal point. This keeps the visible buffer sane without
    // forcing a full parse on every keystroke.
    const allowed = decimals > 0 ? /[^0-9.-]/g : /[^0-9-]/g;
    let cleaned = text.replace(allowed, "");
    // Minus only legal as a leading char. Strip any later ones so the
    // buffer can't drift into "12-3" — parseFloat would silently keep
    // the prefix and the user sees garbage until blur self-corrects.
    if (cleaned.indexOf("-") > 0) {
      cleaned = cleaned[0] + cleaned.slice(1).replace(/-/g, "");
    }
    if (decimals > 0) {
      // At most one decimal point. Drop any extras after the first.
      const firstDot = cleaned.indexOf(".");
      if (firstDot !== -1) {
        cleaned =
          cleaned.slice(0, firstDot + 1) +
          cleaned.slice(firstDot + 1).replace(/\./g, "");
      }
    }
    setBuffer(cleaned);

    const parsed = parseBuffer(cleaned, decimals);
    onChange(parsed === null ? null : clamp(parsed, min, max));
  };

  const handleBlur = () => {
    setFocused(false);
    // Re-format on blur so the buffer mirrors the canonical numeric form.
    const parsed = parseBuffer(buffer, decimals);
    const next = parsed === null ? null : clamp(parsed, min, max);
    setBuffer(formatForDisplay(next, decimals));
    if (next !== value) onChange(next);
  };

  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon}
      labelHint={labelHint}
      error={error}
      hint={hint}
      required={required}
      disabled={disabled}
      loading={loading}
      testID={testID}
    >
      <MoonFieldInput hasError={!!error} focused={focused} paddingY={12}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          {prefix ? (
            <Text
              style={{
                fontFamily: typography.families.sansMedium,
                fontSize,
                color: colors.textLight,
              }}
            >
              {prefix}
            </Text>
          ) : null}
          <TextInput
            value={buffer}
            onChangeText={handleChangeText}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            editable={!disabled}
            keyboardType={decimals > 0 ? "decimal-pad" : "number-pad"}
            placeholder={placeholder}
            placeholderTextColor={colors.textFaint}
            {...textInputProps}
            style={{
              flex: 1,
              fontFamily: typography.families.sans,
              fontSize,
              color: colors.text,
            }}
          />
          {suffix ? (
            <Text
              style={{
                fontFamily: typography.families.sansMedium,
                fontSize,
                color: colors.textLight,
              }}
            >
              {suffix}
            </Text>
          ) : null}
        </View>
      </MoonFieldInput>
    </MoonFormField>
  );
}
